import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';

/**
 * OTPVerificationModal
 * Sends an OTP to the customer's registered mobile number (via /api/auth/resend-otp)
 * and verifies it (via /api/auth/verify-otp) before allowing a sensitive action.
 *
 * Props:
 *  isOpen      – boolean, controls visibility
 *  onSuccess   – callback fired after successful OTP verification
 *  onCancel    – callback fired when user closes / cancels
 *  actionLabel – label shown on the confirm button, e.g. "Save Settings"
 */
const OTPVerificationModal = ({ isOpen, onSuccess, onCancel, actionLabel = 'Proceed' }) => {
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const inputRefs = useRef([]);

  // Send OTP automatically when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtpValues(['', '', '', '', '', '']);
      setOtpSent(false);
      setResendCooldown(0);
      sendOTP();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => setResendCooldown((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  const sendOTP = async (isResend = false) => {
    setIsSending(true);
    try {
      const response = await api.post('/api/auth/resend-otp', {});
      // Backend issues a new access token (with new OTP embedded in claims) — persist it
      if (response?.data?.accessToken) {
        localStorage.setItem('accessToken', response.data.accessToken);
      }
      setOtpSent(true);
      setResendCooldown(30);
      if (isResend) {
        toast.info('OTP resent to your mobile number.');
      } else {
        toast.success('OTP sent to your registered mobile number.');
      }
      setTimeout(() => inputRefs.current[0]?.focus(), 150);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleInputChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newValues = [...otpValues];
    newValues[index] = value;
    setOtpValues(newValues);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d{6}$/.test(pasted)) {
      setOtpValues(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    const otp = otpValues.join('');
    if (otp.length !== 6) {
      toast.error('Please enter a complete 6-digit OTP.');
      return;
    }
    setIsVerifying(true);
    try {
      await api.post('/api/auth/verify-otp', { otp });
      toast.success('OTP verified successfully!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP. Please try again.');
      setOtpValues(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setIsVerifying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.25 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full border border-slate-200 dark:border-gray-700"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-green-600 to-green-700 p-2 rounded-xl shadow-md">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">OTP Verification</h3>
          </div>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Description */}
        <p className="text-slate-600 dark:text-slate-400 text-sm mb-6 text-center">
          {isSending
            ? 'Sending OTP to your registered mobile number…'
            : otpSent
            ? 'Enter the 6-digit OTP sent to your registered mobile number to confirm this action.'
            : 'Preparing to send OTP…'}
        </p>

        {/* Spinner while sending */}
        {isSending ? (
          <div className="flex justify-center py-6">
            <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* OTP Input Boxes */}
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">OTP Code</label>
            <div className="flex justify-center space-x-2 mb-6">
              {otpValues.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  value={value}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  maxLength={1}
                  aria-label={`OTP digit ${index + 1}`}
                  className="w-12 h-12 border border-slate-300 rounded-xl bg-white/80 backdrop-blur-sm outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-lg font-mono"
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={handleVerify}
              disabled={isVerifying || otpValues.join('').length !== 6}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-xl font-medium hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl mb-4"
            >
              {isVerifying ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying…</span>
                </div>
              ) : (
                `Verify & ${actionLabel}`
              )}
            </button>

            {/* Cancel / Resend row */}
            <div className="flex items-center justify-between">
              <button
                onClick={onCancel}
                className="text-slate-500 dark:text-slate-400 text-sm hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => sendOTP(true)}
                disabled={resendCooldown > 0 || isSending}
                className={`text-green-600 font-medium transition-colors ${
                  resendCooldown > 0 || isSending
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:text-green-700'
                }`}
              >
                {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default OTPVerificationModal;
